import { useState, useEffect } from "react";
import {
  CalculateCostsResponseSchema,
  EvaluatedPricingLine,
  Item,
} from "@forgepoint/shared";
import { fetchApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Settings2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useItemVersionMs } from "./itemsStore";

/** A model calculation (fpCalculation field) the summary can surface. */
export interface FooterCalcField {
  key: string;
  label: string;
  suffix?: string;
}

interface CostRow {
  quantity: string;
  unitCost: string | null;
  unitPrice: string | null;
  totalCost: string | null;
  totalPrice: string | null;
  pricingLines: EvaluatedPricingLine[];
}

/** A distinct pricing line offered as a summary choice. */
interface PricingLineChoice {
  /** Stable internal id (used for selection + storage). */
  name: string;
  /** User-facing label (falls back to name when absent). */
  description?: string;
  phase: string;
}

/** Built-in per-quantity outputs (everything else is a model calculation). */
const PER_QTY_OUTPUTS: {
  id: string;
  label: string;
  get: (c: CostRow) => string | null;
}[] = [
  { id: "unit_cost", label: "Unit Cost", get: (c) => c.unitCost },
  { id: "unit_price", label: "Unit Price", get: (c) => c.unitPrice },
  { id: "total_cost", label: "Total Cost", get: (c) => c.totalCost },
  { id: "total_price", label: "Total Price", get: (c) => c.totalPrice },
];

const DEFAULT_OUTPUTS = ["unit_cost", "unit_price"];
const storageKey = (priceModelId: string) =>
  `pm-footer-outputs:${priceModelId}`;

function loadSelected(priceModelId: string): string[] {
  try {
    const raw = localStorage.getItem(storageKey(priceModelId));
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed;
      }
    }
  } catch {
    // ignore malformed storage
  }
  return DEFAULT_OUTPUTS;
}

const CURRENCY_FORMAT: Intl.NumberFormatOptions = {
  minimumFractionDigits: 2,
  maximumFractionDigits: 3,
};

/**
 * Formats a decimal string as USD currency, or "—" when absent/unparseable.
 * @param value - The decimal string to format.
 * @returns The formatted currency (e.g. "$1,234.50") or "—".
 */
function formatCurrency(value: string | null): string {
  const n = value ? parseFloat(value) : NaN;
  if (isNaN(n)) {
    return "—";
  }
  return `$${n.toLocaleString("en-US", CURRENCY_FORMAT)}`;
}

/**
 * Signed currency for pricing-line deltas (the line's contribution), e.g. "+$0.60".
 * @param value - The decimal string to format.
 * @returns The signed formatted currency or "—".
 */
function formatSignedCurrency(value: string | null): string {
  const n = value ? parseFloat(value) : NaN;
  if (isNaN(n)) {
    return "—";
  }
  const abs = Math.abs(n).toLocaleString("en-US", CURRENCY_FORMAT);
  return `${n < 0 ? "-" : "+"}$${abs}`;
}

/**
 * Fetches a price model's per-quantity costs from the `calculate-costs`
 * endpoint (the same one CostPreview / BOMWrapper use), refetching whenever the
 * item's version moves. Shared so the static summary and the floating recalc
 * bar stay in sync off a single request.
 */
export function usePriceModelCosts(
  itemId: string | null | undefined,
  quantities: string[],
): { costs: CostRow[]; loading: boolean } {
  const [costs, setCosts] = useState<CostRow[]>([]);
  const [loading, setLoading] = useState(false);
  const itemVersionMs = useItemVersionMs(itemId);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!itemId) {
        setCosts([]);
        return;
      }
      setLoading(true);
      try {
        const response = await fetchApi(
          `items/${itemId}/calculate-costs`,
          "POST",
          { body: { quantities } },
        );
        if (cancelled) {
          return;
        }
        if (response.ok && response.data) {
          const data = CalculateCostsResponseSchema.parse(response.data);
          setCosts(
            data.costs.map((c) => ({
              quantity: c.quantity,
              unitCost: c.unitCost,
              unitPrice: c.unitPrice,
              totalCost: c.totalCost,
              totalPrice: c.totalPrice,
              pricingLines: c.pricingLines ?? [],
            })),
          );
        } else {
          setCosts([]);
        }
      } catch {
        if (!cancelled) {
          setCosts([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [itemId, itemVersionMs, quantities]);

  return { costs, loading };
}

interface PriceModelSummaryProps {
  item: Item;
  quantities: string[];
  costs: CostRow[];
  loading: boolean;
  priceModelId: string;
  calculationFields: FooterCalcField[];
}

/**
 * Static, at-rest summary that heads the detailed output. Shows the model's key
 * outputs — unit cost / unit price pinned by default, then configurable via the
 * gear (a per-user override in localStorage). Per-quantity metrics render as a
 * table in waterfall order (cost → pricing lines → price); model calculations
 * render as scalar chips.
 */
export function PriceModelSummary({
  item,
  quantities,
  costs,
  loading,
  priceModelId,
  calculationFields,
}: PriceModelSummaryProps) {
  const [selectedIds, setSelectedIds] = useState<string[]>(() =>
    loadSelected(priceModelId),
  );

  useEffect(() => {
    setSelectedIds(loadSelected(priceModelId));
  }, [priceModelId]);

  const toggleOutput = (id: string) => {
    setSelectedIds((prev) => {
      const next = prev.includes(id)
        ? prev.filter((x) => x !== id)
        : [...prev, id];
      try {
        localStorage.setItem(storageKey(priceModelId), JSON.stringify(next));
      } catch {
        // ignore storage failures
      }
      return next;
    });
  };

  const costByQty = new Map(costs.map((c) => [c.quantity, c]));
  const calcValues = item.priceModelCalculations ?? {};

  const availablePricingLines: PricingLineChoice[] = [];
  const seenLines = new Set<string>();
  for (const c of costs) {
    for (const pl of c.pricingLines) {
      if (!seenLines.has(pl.name)) {
        seenLines.add(pl.name);
        availablePricingLines.push({
          name: pl.name,
          description: pl.description,
          phase: pl.phase,
        });
      }
    }
  }

  const shownCalcs = calculationFields.filter((f) =>
    selectedIds.includes(`calc:${f.key}`),
  );
  const shownPricingLines = availablePricingLines.filter((pl) =>
    selectedIds.includes(`pl:${pl.name}`),
  );

  // Table rows render in waterfall order, matching the on-screen breakdown.
  const costLines = shownPricingLines.filter((pl) => pl.phase === "COST");
  const priceLines = shownPricingLines.filter((pl) => pl.phase === "PRICE");
  type FooterRow =
    | { kind: "metric"; output: (typeof PER_QTY_OUTPUTS)[number] }
    | { kind: "pl"; pl: PricingLineChoice };
  const tableRows: FooterRow[] = [];
  const pushMetric = (id: string) => {
    if (!selectedIds.includes(id)) {
      return;
    }
    const output = PER_QTY_OUTPUTS.find((o) => o.id === id);
    if (output) {
      tableRows.push({ kind: "metric", output });
    }
  };
  pushMetric("unit_cost");
  pushMetric("total_cost");
  costLines.forEach((pl) => tableRows.push({ kind: "pl", pl }));
  priceLines.forEach((pl) => tableRows.push({ kind: "pl", pl }));
  pushMetric("total_price");
  pushMetric("unit_price");

  return (
    <div className="mb-6 rounded-lg border bg-muted px-4 py-3 shadow-sm">
      <div className="flex items-center gap-4">
        <div className="flex shrink-0 items-center self-stretch border-r pr-4 text-xs font-bold uppercase tracking-wider text-muted-foreground">
          Summary
        </div>
        <div
          className={cn(
            "min-w-0 flex-1 space-y-1.5 overflow-x-auto transition-opacity",
            loading && "opacity-50",
          )}
        >
          {shownCalcs.length > 0 && (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
              {shownCalcs.map((f) => {
                const label = f.label;
                const suffix = f.suffix;
                const value = calcValues[f.key];
                return (
                  <span key={f.key} className="whitespace-nowrap">
                    <span className="text-muted-foreground">
                      {label ?? f.label}:{" "}
                    </span>
                    <span className="font-medium tabular-nums">
                      {value ?? "—"}
                      {suffix ? ` ${suffix}` : ""}
                    </span>
                  </span>
                );
              })}
            </div>
          )}

          {tableRows.length > 0 && (
            <table className="text-xs tabular-nums">
              <tbody>
                <tr className="text-muted-foreground">
                  <th
                    scope="col"
                    className="pr-4 text-left font-medium uppercase tracking-wide"
                  >
                    Qty
                  </th>
                  {quantities.map((q) => (
                    <th
                      key={q}
                      scope="col"
                      className="px-3 text-right font-medium"
                    >
                      {q}
                    </th>
                  ))}
                </tr>
                {tableRows.map((row) =>
                  row.kind === "metric" ? (
                    <tr
                      key={row.output.id}
                      className="motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-top-1 motion-safe:duration-200"
                    >
                      <th
                        scope="row"
                        className="pr-4 text-left font-medium text-muted-foreground"
                      >
                        {row.output.label}
                      </th>
                      {quantities.map((q) => {
                        const c = costByQty.get(q);
                        const isPriceTotal =
                          row.output.id === "unit_price" ||
                          row.output.id === "total_price";
                        return (
                          <td
                            key={q}
                            className={cn(
                              "px-3 text-right",
                              isPriceTotal && "font-semibold text-foreground",
                            )}
                          >
                            {formatCurrency(c ? row.output.get(c) : null)}
                          </td>
                        );
                      })}
                    </tr>
                  ) : (
                    <tr
                      key={`pl:${row.pl.name}`}
                      className="motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-top-1 motion-safe:duration-200"
                    >
                      <th
                        scope="row"
                        className="pr-4 text-left font-normal text-muted-foreground"
                      >
                        {row.pl.description ?? row.pl.name}
                      </th>
                      {quantities.map((q) => {
                        const line = costByQty
                          .get(q)
                          ?.pricingLines.find((l) => l.name === row.pl.name);
                        return (
                          <td
                            key={q}
                            className="px-3 text-right text-muted-foreground"
                          >
                            {formatSignedCurrency(line?.delta ?? null)}
                          </td>
                        );
                      })}
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          )}

          {tableRows.length === 0 && shownCalcs.length === 0 && (
            <p className="text-xs text-muted-foreground">
              No outputs selected — pick some from the menu.
            </p>
          )}
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              className="group shrink-0"
              aria-label="Configure summary outputs"
            >
              <Settings2 className="h-4 w-4 transition-transform duration-200 motion-safe:group-hover:rotate-90" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>Per quantity</DropdownMenuLabel>
            {PER_QTY_OUTPUTS.map((o) => (
              <DropdownMenuCheckboxItem
                key={o.id}
                checked={selectedIds.includes(o.id)}
                onCheckedChange={() => toggleOutput(o.id)}
                onSelect={(e) => e.preventDefault()}
              >
                {o.label}
              </DropdownMenuCheckboxItem>
            ))}
            {availablePricingLines.length > 0 && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuLabel>Pricing lines</DropdownMenuLabel>
                {availablePricingLines.map((pl) => {
                  const id = `pl:${pl.name}`;
                  return (
                    <DropdownMenuCheckboxItem
                      key={id}
                      checked={selectedIds.includes(id)}
                      onCheckedChange={() => toggleOutput(id)}
                      onSelect={(e) => e.preventDefault()}
                    >
                      {pl.description ?? pl.name}
                    </DropdownMenuCheckboxItem>
                  );
                })}
              </>
            )}
            {calculationFields.length > 0 && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuLabel>Calculations</DropdownMenuLabel>
                {calculationFields.map((f) => {
                  const id = `calc:${f.key}`;
                  return (
                    <DropdownMenuCheckboxItem
                      key={id}
                      checked={selectedIds.includes(id)}
                      onCheckedChange={() => toggleOutput(id)}
                      onSelect={(e) => e.preventDefault()}
                    >
                      {f.label}
                    </DropdownMenuCheckboxItem>
                  );
                })}
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
